/**
 * Notifications domain types for the admin-panel.
 *
 * Runtime values (`NotificationCategory`, `WATCHED_CRONS`) are duplicated here
 * rather than re-exported from `@repo/back-config`. Re-exporting runtime
 * values would pull back-config's full module tree into the browser bundle,
 * including code that imports Node built-ins (crypto, fs) that Vite cannot
 * safely externalize. A parity test in `types.parity.test.ts` asserts these
 * stay aligned with the back-config source.
 *
 * Pure types, on the other hand, are erased at build time and are safe to
 * pull from `@repo/back-config` directly.
 */

// ── Runtime values (local copies — see parity test) ────────────────────

/**
 * Notification intent categories for caps and routing. Mirrors
 * `@repo/back-config` `NotificationCategory` at runtime; validated by
 * `types.parity.test.ts`. Kept as an `enum` (rather than `as const`) so
 * its type identity matches back-config's — `UserNotificationOverrides.
 * mutedCategories` is typed as the back-config enum, and a structural
 * string-literal union isn't assignable to it.
 */
export enum NotificationCategory {
  REMINDER = 'reminder',
  SOCIAL = 'social',
  PROGRESSION = 'progression',
  SYSTEM = 'system',
  ENGAGEMENT = 'engagement',
}

/**
 * Cron jobs the health dashboard surfaces heartbeats for. Mirrors
 * `@repo/back-config` `WATCHED_CRONS`.
 */
export const WATCHED_CRONS: readonly string[] = [
  'EngagementReconcilerCron',
  'EngagementSignalRefreshCron',
  'EngagementWindowRefreshCron',
  'IntentOrchestratorCron',
  'IntentExpirationSweepCron',
  'IntentRetentionSweepCron',
  'HealthMetricsRollupCron',
  'AlertEvaluatorCron',
] as const;

// ── Types (erased at build time, safe to import from back-config) ────────

import type {
  AdminAuditTargetType,
  NotificationAdminAudit,
  NotificationAlertFiring,
  AlertDispatchChannel,
  NotificationAlertPolicy,
  AlertSeverity,
  AlertComparison,
  AlertQuerySource,
  NotificationDashboardSnapshot,
  DashboardCategoryStat,
  DashboardJourneyStat,
  DashboardQueueDepth,
  DashboardDeferralStats,
  DashboardPersonalizationStats,
  DashboardKpis,
  DashboardKpiTrend,
  DashboardEngagementScoreDistribution,
  JourneyDefinition,
  JourneyStep,
  JourneyStepVariant,
  JourneyStopCondition,
  JourneyTrigger,
  UserNotificationOverrides,
  UserForcedWindow,
  NotificationGlobalConfig,
} from '@repo/back-config';

export type {
  AdminAuditTargetType,
  NotificationAdminAudit,
  NotificationAlertFiring,
  AlertDispatchChannel,
  NotificationAlertPolicy,
  AlertSeverity,
  AlertComparison,
  AlertQuerySource,
  NotificationDashboardSnapshot,
  DashboardCategoryStat,
  DashboardJourneyStat,
  DashboardQueueDepth,
  DashboardDeferralStats,
  DashboardPersonalizationStats,
  DashboardKpis,
  DashboardKpiTrend,
  DashboardEngagementScoreDistribution,
  JourneyDefinition,
  JourneyStep,
  JourneyStepVariant,
  JourneyStopCondition,
  JourneyTrigger,
  UserNotificationOverrides,
  UserForcedWindow,
  NotificationGlobalConfig,
};

export interface UserNotificationProfile {
  userId: string;
  email?: string;
  timeZone?: string;
  engagementScore?: number;
  engagementWindows?: unknown;
  notificationOverrides?: UserNotificationOverrides;
}

export interface AuditListResponse {
  entries: NotificationAdminAudit[];
  nextCursor?: string | null;
}
